/**
 * RuleSetImportModal — 2-step wizard for importing a new RuleSet.
 *
 * Step 1: Core identity (ID, name, jurisdiction, safety level, industries, effective date)
 * Step 2: Source legislation + member rule IDs + affected workflows
 *
 * Calls `useImportRuleSetMutation` (generated after codegen).
 *
 * DRR-096: effective_date required for temporal validity.
 * DRR-112: Backend dual-writes Policy primitive on import.
 * DRR-055: Dumb frontend — no local validation domain logic.
 */

import { useState } from 'react';
import {
  Modal,
  Stepper,
  TextInput,
  Textarea,
  Select,
  MultiSelect,
  Switch,
  Button,
  Group,
  Stack,
  Text,
  Alert,
} from '@mantine/core';
import { IconAlertCircle } from '@tabler/icons-react';
import { useImportRuleSetMutation } from '@/generated/graphql';

// ============================================================================
// TYPES
// ============================================================================

interface ImportFormState {
  // Step 1
  rulesetId: string;
  name: string;
  displayName: string;
  description: string;
  safetyLevel: string;
  isLifeCritical: boolean;
  jurisdiction: string;
  category: string;
  setting: string;
  appliesToIndustries: string[];
  version: string;
  effectiveDate: string;
  complianceFrameworks: string[];
  // Step 2
  ruleIds: string;
  affectsWorkflows: string;
  sourceLegislationStatute: string;
  sourceLegislationAuthority: string;
  sourceLegislationShortCite: string;
  sourceLegislationUrl: string;
}

const EMPTY_FORM: ImportFormState = {
  rulesetId: '',
  name: '',
  displayName: '',
  description: '',
  safetyLevel: 'standard',
  isLifeCritical: false,
  jurisdiction: '',
  category: 'compliance',
  setting: 'general',
  appliesToIndustries: [],
  version: '1.0',
  effectiveDate: '',
  complianceFrameworks: [],
  ruleIds: '',
  affectsWorkflows: '',
  sourceLegislationStatute: '',
  sourceLegislationAuthority: '',
  sourceLegislationShortCite: '',
  sourceLegislationUrl: '',
};

// ============================================================================
// COMPONENT
// ============================================================================

interface RuleSetImportModalProps {
  opened: boolean;
  onClose: () => void;
  onImported: () => void;
}

export function RuleSetImportModal({ opened, onClose, onImported }: RuleSetImportModalProps) {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState<ImportFormState>(EMPTY_FORM);
  const [importError, setImportError] = useState<string | null>(null);

  const [importRuleSet, { loading }] = useImportRuleSetMutation();

  function set<K extends keyof ImportFormState>(field: K, value: ImportFormState[K]) {
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  function handleClose() {
    setStep(0);
    setForm(EMPTY_FORM);
    setImportError(null);
    onClose();
  }

  async function handleSubmit() {
    setImportError(null);
    try {
      const sourceLeg =
        form.sourceLegislationStatute
          ? {
              statute: form.sourceLegislationStatute,
              shortCite: form.sourceLegislationShortCite,
              sections: [],
              authority: form.sourceLegislationAuthority,
              regnodeRef: null,
              lastAmended: null,
              url: form.sourceLegislationUrl || null,
            }
          : null;

      await importRuleSet({
        variables: {
          input: {
            rulesetId: form.rulesetId,
            name: form.name,
            displayName: form.displayName,
            description: form.description,
            safetyLevel: form.safetyLevel,
            isLifeCritical: form.isLifeCritical,
            jurisdiction: form.jurisdiction,
            category: form.category,
            setting: form.setting,
            appliesToIndustries: form.appliesToIndustries,
            version: form.version || null,
            effectiveDate: form.effectiveDate || null,
            complianceFrameworks: form.complianceFrameworks,
            ruleIds: form.ruleIds.split(',').map((s) => s.trim()).filter(Boolean),
            affectsWorkflows: form.affectsWorkflows.split(',').map((s) => s.trim()).filter(Boolean),
            sourceLegislation: sourceLeg,
          },
        },
      });
      onImported();
      handleClose();
    } catch (err) {
      setImportError(String(err));
    }
  }

  const step1Valid =
    form.rulesetId.trim() !== '' &&
    form.displayName.trim() !== '' &&
    form.jurisdiction.trim() !== '';

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title="Import New RuleSet"
      size="lg"
    >
      <Stepper active={step} onStepClick={setStep} size="sm" mb="lg">
        <Stepper.Step label="Core Fields" description="Identity & jurisdiction" />
        <Stepper.Step label="Legislation" description="Source statute & rules" />
      </Stepper>

      {step === 0 && (
        <Stack gap="sm">
          <TextInput
            label="RuleSet ID"
            placeholder="rs-4-1-005"
            description="Scope-encoded ID per DRR-089 (e.g., rs-{subgraph}-{category}-{seq})"
            required
            value={form.rulesetId}
            onChange={(e) => set('rulesetId', e.target.value)}
          />
          <TextInput
            label="Machine Name"
            placeholder="mn-144g-memory-care"
            required
            value={form.name}
            onChange={(e) => set('name', e.target.value)}
          />
          <TextInput
            label="Display Name"
            placeholder="MN 144G — Memory Care Unit Standards"
            required
            value={form.displayName}
            onChange={(e) => set('displayName', e.target.value)}
          />
          <Textarea
            label="Description"
            placeholder="Describe what this RuleSet covers..."
            rows={3}
            value={form.description}
            onChange={(e) => set('description', e.target.value)}
          />
          <Group grow>
            <TextInput
              label="Jurisdiction"
              placeholder="MN"
              required
              value={form.jurisdiction}
              onChange={(e) => set('jurisdiction', e.target.value)}
            />
            <Select
              label="Safety Level"
              data={['standard', 'high', 'critical']}
              value={form.safetyLevel}
              onChange={(v) => set('safetyLevel', v ?? 'standard')}
            />
          </Group>
          <Group grow>
            <TextInput
              label="Category"
              placeholder="compliance"
              value={form.category}
              onChange={(e) => set('category', e.target.value)}
            />
            <TextInput
              label="Setting"
              placeholder="assisted_living"
              value={form.setting}
              onChange={(e) => set('setting', e.target.value)}
            />
          </Group>
          <MultiSelect
            label="Applies to Industries"
            placeholder="Select industries"
            data={['assisted_living', 'healthcare', 'home_care', 'hospice', 'financial', 'legal', 'payroll']}
            value={form.appliesToIndustries}
            onChange={(v) => set('appliesToIndustries', v)}
          />
          <Group grow>
            <TextInput
              label="Version"
              placeholder="1.0"
              value={form.version}
              onChange={(e) => set('version', e.target.value)}
            />
            <TextInput
              label="Effective Date"
              placeholder="2025-01-01"
              description="ISO 8601 date (DRR-096)"
              value={form.effectiveDate}
              onChange={(e) => set('effectiveDate', e.target.value)}
            />
          </Group>
          <Switch
            label="Life-Critical RuleSet"
            description="Contains rules where non-compliance could cause patient harm"
            checked={form.isLifeCritical}
            onChange={(e) => set('isLifeCritical', e.target.checked)}
          />
        </Stack>
      )}

      {step === 1 && (
        <Stack gap="sm">
          <TextInput
            label="Source Statute"
            placeholder="MN Statutes Chapter 144G"
            value={form.sourceLegislationStatute}
            onChange={(e) => set('sourceLegislationStatute', e.target.value)}
          />
          <Group grow>
            <TextInput
              label="Short Citation"
              placeholder="MN 144G"
              value={form.sourceLegislationShortCite}
              onChange={(e) => set('sourceLegislationShortCite', e.target.value)}
            />
            <TextInput
              label="Regulatory Authority"
              placeholder="Minnesota Department of Health"
              value={form.sourceLegislationAuthority}
              onChange={(e) => set('sourceLegislationAuthority', e.target.value)}
            />
          </Group>
          <TextInput
            label="Statute URL"
            placeholder="https://..."
            value={form.sourceLegislationUrl}
            onChange={(e) => set('sourceLegislationUrl', e.target.value)}
          />
          <Textarea
            label="Member Rule IDs"
            placeholder="rl-4-1-001, rl-4-1-002, rl-4-1-003"
            description="Comma-separated rule config IDs in this bundle"
            rows={3}
            value={form.ruleIds}
            onChange={(e) => set('ruleIds', e.target.value)}
          />
          <Textarea
            label="Affected Workflow IDs"
            placeholder="wf-0-0-001, wf-4-0-002"
            description="Comma-separated workflow IDs impacted by this RuleSet"
            rows={2}
            value={form.affectsWorkflows}
            onChange={(e) => set('affectsWorkflows', e.target.value)}
          />
          <MultiSelect
            label="Compliance Frameworks"
            placeholder="Add frameworks"
            data={['MN_MDH_ALF', 'MN_MDH_SNF', 'CMS_CoP', 'HIPAA', 'OSHA']}
            value={form.complianceFrameworks}
            onChange={(v) => set('complianceFrameworks', v)}
            searchable
          />

          {importError && (
            <Alert color="red" icon={<IconAlertCircle size={16} />}>
              {importError}
            </Alert>
          )}
        </Stack>
      )}

      <Group justify="space-between" mt="xl">
        <Button variant="subtle" onClick={step === 0 ? handleClose : () => setStep(0)}>
          {step === 0 ? 'Cancel' : 'Back'}
        </Button>
        {step === 0 ? (
          <Button disabled={!step1Valid} onClick={() => setStep(1)}>
            Next
          </Button>
        ) : (
          <Button loading={loading} onClick={handleSubmit}>
            Import RuleSet
          </Button>
        )}
      </Group>
    </Modal>
  );
}
